export enum NotificationType {
  Workflow = 'workflow',
  Scheduler = 'scheduler',
  Interface = 'interface',
  Code = 'code',
  FileManager = 'file_manager',
  VersionManager = 'version_manager',
  ScenarioManager = 'scenario_manager',
  AppConsolidation = 'app_consolidation',
  AutomationTests = 'automation_tests',
  General = 'general'
}
export interface SchedulerExtraParams {
  task_id: string
}
export interface InterfaceExtraParams {
  interface_id: string
}
export interface MessageTargets {
  users: string[]
  departments: string[]
  teams: string[]
}

export interface MessagePerson {
  id: number
  name: string
}

export interface UserMessage {
  id: number
  body: string
  created_at: string
  // Null once the sender left the company.
  sender: MessagePerson | null
  to: MessageTargets
  target_label: string | null
  send_email: boolean
  // For a reply: the message it answers, and who sent that message.
  parent: number | null
  parent_sender_id: number | null
}

export type Notification = {
  id: number
  usercompany: number
  user_message: UserMessage | null
  seen_at: string | null
  read_date: string | null
  application_id: string
  app_path: string | null
  app_name: string | null
  version: string | null
  date: string
  message: string
  extra_params?: object | SchedulerExtraParams | InterfaceExtraParams
  notification_type: NotificationType
}

export enum NotificationScope {
  All = 'all',
  CurrentApp = 'current_app'
}

export enum NotificationKind {
  Message = 'message',
  System = 'system'
}

export interface NotificationsParams {
  page: number
  page_size: number
  order_by: string
  scope: NotificationScope
  kind?: NotificationKind
  apply_filter?: string
}

export interface SentMessage {
  id: number
  body: string
  created_at: string
  to: MessageTargets
  recipients_count: number
  seen_count: number
  replies_count: number
  send_email: boolean
  application_id: string | null
  app_path: string | null
  app_name: string | null
  version: string | null
  notification_type: NotificationType
  extra_params: InterfaceExtraParams | null
  target_label: string | null
}

export interface ThreadReply {
  id: number
  body: string
  created_at: string
  sender: MessagePerson | null
  mine: boolean
}

export interface Conversation {
  // Null for the people who left the company, all in one conversation.
  partner: MessagePerson | null
  replies: ThreadReply[]
  unread: number
}

export interface MessageThread {
  message: UserMessage
  is_sender: boolean
  // The caller's own notification of the message, when they received it.
  notification: Notification | null
  conversations: Conversation[]
}

export interface MessageReader {
  id: number
  name: string
  seen_at: string | null
}

export interface MessageReadersGroup {
  kind: 'user' | 'department' | 'team'
  name: string | null
  readers: MessageReader[]
}

export interface SentMessageDetail {
  message: SentMessage
  groups: MessageReadersGroup[]
}

export interface RecipientPerson {
  id: number
  name: string
  department: string | null
}

export interface RecipientGroup {
  id: number
  name: string
  members: number
}

export interface RecipientSearchResult {
  people: RecipientPerson[]
  departments: RecipientGroup[]
  teams: RecipientGroup[]
}

export interface MessageRecipients {
  user_ids: number[]
  department_ids: number[]
  team_ids: number[]
}

export interface MessagePreview {
  recipients: number
  limit: number
}

export interface SendMessageData extends MessageRecipients {
  body: string
  send_email: boolean
  notification_type: NotificationType
  extra_params?: InterfaceExtraParams
  target_label?: string
  link_app: boolean
  link_version: boolean
}

export interface SendMessageError {
  code:
    | 'too_many_recipients'
    | 'no_recipients'
    | 'empty_body'
    | 'body_too_long'
    | 'invalid_recipients'
  recipients?: string
  limit?: string
}

export interface ChangeNotificationData {
  notification_ids: number[]
  switch_read_date?: boolean
}
export interface NotificationPaginatedResponse<T> {
  results: T[]
  count: number
  total_pages?: number
  unread_count: number
}

// Notifications App/Infrastructure

export interface CreateNotificationData {
  title: string
  description: string
  effective_date: Date
}

export interface NotificationsApp extends CreateNotificationData {
  id: string
  creator_name?: string
  creation_date?: Date
}

export interface AddOrEditNotificationRouteParam {
  [id: string]: string
}

export interface GeneralNotification extends CreateNotificationData {
  id: number
  creation_date: Date
  notification_is_read: boolean
}
